import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Switch,
  Box,
  Divider,
  Stack,
} from "@mui/material";
import { useCookieConsent } from "../../hooks/useCookieConsent";
import { type CookieConsent } from "../../contexts/CookieContext";

interface CookieSettingsFormProps {
  initialConsent: CookieConsent | null;
  onSave: (consent: CookieConsent) => void;
  onCancel: () => void;
}

const CookieSettingsForm: React.FC<CookieSettingsFormProps> = ({
  initialConsent,
  onSave,
  onCancel,
}) => {
  const [preferences, setPreferences] = useState<CookieConsent>(
    initialConsent || {
      essential: true,
      analytics: false,
      marketing: false,
    }
  );

  const handleToggle = (key: keyof CookieConsent) => {
    if (key === "essential") return;
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: "bold" }}>Cookie Preferences</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          When you visit any website, it may store or retrieve information on your browser, mostly
          in the form of cookies. This information might be about you, your preferences or your
          device and is mostly used to make the site work as you expect it to.
        </Typography>

        <Stack spacing={3} sx={{ mt: 2 }}>
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Strictly Necessary Cookies
              </Typography>
              <Switch checked={true} disabled />
            </Box>
            <Typography variant="body2" color="text.secondary">
              These cookies are necessary for the website to function and cannot be switched off in
              our systems. They are usually only set in response to actions made by you which amount
              to a request for services, such as setting your privacy preferences, logging in or
              filling in forms.
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Performance & Analytics
              </Typography>
              <Switch checked={preferences.analytics} onChange={() => handleToggle("analytics")} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              These cookies allow us to count visits and traffic sources so we can measure and
              improve the performance of our site. They help us to know which pages are the most and
              least popular and see how visitors move around the site.
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Marketing & Targeting
              </Typography>
              <Switch checked={preferences.marketing} onChange={() => handleToggle("marketing")} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              These cookies may be set through our site by our advertising partners. They may be
              used by those companies to build a profile of your interests and show you relevant
              adverts on other sites.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={() => onSave(preferences)} variant="contained" color="primary">
          Save Preferences
        </Button>
      </DialogActions>
    </>
  );
};

export const CookieSettingsModal: React.FC = () => {
  const { settingsOpen, setSettingsOpen, saveConsent, consent } = useCookieConsent();

  return (
    <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
      {settingsOpen && (
        <CookieSettingsForm
          initialConsent={consent}
          onSave={saveConsent}
          onCancel={() => setSettingsOpen(false)}
        />
      )}
    </Dialog>
  );
};
